import LockIcon from '@mui/icons-material/Lock';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  Divider,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { recallLotAllocationsToInventoryApi } from 'api/inventory';
import colors from 'common/constants/colors';
import { handleError } from 'store/thunks/errorHandlerThunks';
import { getStateDisplayName } from 'store/utils/shipmentUtils';

import RecallDialogHeader from './RecallDialogHeader';
import RecallHowItWorks from './RecallHowItWorks';
import RecallLotSelector from './RecallLotSelector';
import RecallResultPanel from './RecallResultPanel';

const PANEL_SX = {
  bgcolor: '#f4f6f8',
  borderRadius: 1,
  borderLeft: `4px solid ${colors.dialogCloseNavy}`,
  px: 2,
  py: 1.25,
};

const FIELD_LABEL_SX = {
  fontWeight: 600,
  fontSize: 11,
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  display: 'block',
  mb: 0.75,
};

const ACK_SX = {
  m: 0,
  mt: 2,
  alignItems: 'flex-start',
  px: 1.5,
  py: 1.25,
  border: '1px solid',
  borderColor: colors.inventoryLockOrangeBorder,
  bgcolor: colors.inventoryLockOrangeLight,
  borderRadius: 1,
};

// Never labelled "stock recall" — in a pharmacy system that reads as an FDA drug recall.
const RECALL_REASONS = [
  { value: 'ALLOCATION_RECALL', label: 'Recall allocations to inventory' },
  { value: 'STOCK_REPRIORITIZATION', label: 'Stock reprioritization' },
  { value: 'ALLOCATION_CORRECTION', label: 'Allocation correction' },
];

const facilityLabel = (pharmacy) => {
  if (!pharmacy?.name) return '—';
  return pharmacy.state
    ? `${pharmacy.name} (${getStateDisplayName(pharmacy.state)})`
    : pharmacy.name;
};

const wipOf = (lot) => Number(lot?.wipQty ?? 0);
const sumWip = (lots) => (lots ?? []).reduce((total, lot) => total + wipOf(lot), 0);
const wipOfSummary = (summary) => Number(summary?.result?.totalWipQty ?? 0);

// Open on the first location that is both recallable and holding stock, so the operator does not
// land on one whose only outcome is the count-locked warning.
const defaultPharmacyId = (summaries) => {
  const withStock = summaries.find((s) => !s.item?.isLockForCount && wipOfSummary(s) > 0);
  const unlocked = summaries.find((s) => !s.item?.isLockForCount);
  const chosen = withStock ?? unlocked ?? summaries[0];
  return chosen?.item?.fulfillingPharmacy?.fulfillingPharmacyId ?? '';
};

const RecallAllocationsDialog = ({ open, summaries, onClose, onRecallSuccess }) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [pharmacyId, setPharmacyId] = useState('');
  const [lotMode, setLotMode] = useState('all');
  const [selectedLotIds, setSelectedLotIds] = useState([]);
  const [reason, setReason] = useState(RECALL_REASONS[0].value);
  const [note, setNote] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [outcome, setOutcome] = useState(null);
  const [contentionError, setContentionError] = useState('');

  const first = summaries[0];

  const summariesRef = useRef(summaries);
  summariesRef.current = summaries;

  // Keyed on the OPEN TRANSITION, never on `summaries` identity: the drawer rebuilds that array
  // on every refetch, which would wipe the scope the operator is mid-way through building.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    const justOpened = open && !wasOpenRef.current;
    wasOpenRef.current = open;
    if (!justOpened) return;

    setPharmacyId(defaultPharmacyId(summariesRef.current));
    setLotMode('all');
    setSelectedLotIds([]);
    setReason(RECALL_REASONS[0].value);
    setNote('');
    setAcknowledged(false);
    setSubmitting(false);
    setOutcome(null);
    setContentionError('');
  }, [open]);

  // Display order only — the default location above is still chosen from the drawer's order.
  const sortedSummaries = useMemo(
    () =>
      [...summaries].sort((left, right) =>
        facilityLabel(left.item?.fulfillingPharmacy).localeCompare(
          facilityLabel(right.item?.fulfillingPharmacy),
          undefined,
          { sensitivity: 'base' }
        )
      ),
    [summaries]
  );

  const activeSummary = useMemo(
    () =>
      summaries.find(({ item }) => item.fulfillingPharmacy?.fulfillingPharmacyId === pharmacyId) ??
      null,
    [summaries, pharmacyId]
  );

  // Only lots holding a reservation are offered — a zero-reserved lot is not a choice.
  const reservedLots = useMemo(
    () => (activeSummary?.result?.lots ?? []).filter((lot) => wipOf(lot) > 0),
    [activeSummary]
  );

  const isLocked = Boolean(activeSummary?.item?.isLockForCount);

  const scopedLots = useMemo(() => {
    if (isLocked) return [];
    if (lotMode === 'all') return reservedLots;
    // Against the CURRENT location's lots, so a stale id cannot inflate the total.
    return reservedLots.filter((lot) => selectedLotIds.includes(lot.inventoryLotId));
  }, [isLocked, lotMode, reservedLots, selectedLotIds]);

  const scopedWip = sumWip(scopedLots);
  const scopedLotCount = scopedLots.length;

  // The backend lock is per inventory item, so one busy location must not block the others.
  const recallRunning = Boolean(activeSummary?.result?.recallInProgress);
  const canSubmit = scopedLotCount > 0 && acknowledged && !recallRunning && !isLocked;

  const handleClose = () => {
    if (submitting) return;
    // The header X must refresh the drawer exactly as the footer Close does, or a successful
    // recall leaves the panel showing pre-recall totals.
    if (outcome) onRecallSuccess();
    onClose();
  };

  // Lot ids belong to one location — a carried-over selection would name lots that are not there.
  const handleLocationChange = (nextPharmacyId) => {
    setPharmacyId(nextPharmacyId);
    setSelectedLotIds([]);
  };

  const handleLotModeChange = (nextLotMode) => {
    setLotMode(nextLotMode);
    setSelectedLotIds([]);
  };

  const handleToggleLot = (inventoryLotId) =>
    setSelectedLotIds((prev) =>
      prev.includes(inventoryLotId)
        ? prev.filter((id) => id !== inventoryLotId)
        : [...prev, inventoryLotId]
    );

  const handleToggleAll = (checked) =>
    setSelectedLotIds(checked ? reservedLots.map((lot) => lot.inventoryLotId) : []);

  // Omitting inventoryLotIds lets the SERVER decide what "all lots" covers, rather than this
  // filtered on-screen list. Never send [] — the server rejects it so a bug cannot mean "all".
  const buildInput = () => ({
    ndc: first?.item?.ndc,
    fulfillingPharmacyIds: [pharmacyId],
    ...(lotMode === 'selected' && { inventoryLotIds: selectedLotIds }),
    reason,
    ...(note.trim() && { note: note.trim() }),
  });

  const handleSubmit = () => {
    setSubmitting(true);
    setContentionError('');

    recallLotAllocationsToInventoryApi(buildInput())
      .then((result) => {
        setSubmitting(false);
        setOutcome(result);
        // A queued run has moved nothing yet — the result panel says so, and the toast has to
        // agree or the operator reads two different outcomes for the same action.
        const units = result?.quantityReturned ?? 0;
        enqueueSnackbar(
          result?.status === 'QUEUED'
            ? `${units} units queued for return to inventory`
            : `${units} units returned to inventory`,
          { variant: 'success' }
        );
      })
      .catch((error) => {
        setSubmitting(false);
        const errorCode = error?.response?.errors?.[0]?.extensions?.code;
        // Contention is an expected outcome, not a fault — keep the scope so they can retry.
        if (errorCode === 'LOT_RECALL_ALREADY_IN_PROGRESS') {
          setContentionError(
            'A recall is already in progress for this drug at this location. ' +
              'Wait for it to finish, then try again.'
          );
        } else if (errorCode === 'LOT_RECALL_LOCK_UNAVAILABLE') {
          setContentionError(
            'Could not confirm that no other recall is running. Please try again in a moment.'
          );
        } else {
          dispatch(handleError({ error }));
        }
      });
  };

  if (!first) return null;

  const renderLocationScope = () => {
    if (!activeSummary) return null;

    if (isLocked) {
      return (
        <Alert severity='warning'>
          This location is locked for a physical count. Stock cannot be recalled until the count is
          closed.
        </Alert>
      );
    }

    if (reservedLots.length === 0) {
      return (
        <Typography variant='body2' color='text.secondary' sx={{ textAlign: 'center', py: 5 }}>
          No reserved stock at this location.
        </Typography>
      );
    }

    return (
      <RecallLotSelector
        lotMode={lotMode}
        reservedLots={reservedLots}
        selectedLotIds={selectedLotIds}
        onLotModeChange={handleLotModeChange}
        onToggleLot={handleToggleLot}
        onToggleAll={handleToggleAll}
      />
    );
  };

  const renderForm = () => (
    <Box sx={{ px: 2.5, py: 2 }}>
      {recallRunning && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          A recall is already running for this drug at this location. Wait for it to finish before
          starting another.
        </Alert>
      )}
      {contentionError && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {contentionError}
        </Alert>
      )}

      {/* One location per recall — lot numbers are only unique within a facility. */}
      <Box sx={{ mb: 2 }}>
        <Typography component='span' sx={FIELD_LABEL_SX}>
          Location *
        </Typography>
        <TextField
          select
          size='small'
          value={pharmacyId}
          onChange={(event) => handleLocationChange(event.target.value)}
          sx={{ width: 380 }}
          inputProps={{ 'aria-label': 'Location' }}
        >
          {sortedSummaries.map(({ item, result }) => (
            <MenuItem
              key={item.inventoryItemId}
              value={item.fulfillingPharmacy?.fulfillingPharmacyId}
              disabled={item.isLockForCount}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: '100%' }}>
                {item.isLockForCount && (
                  <LockIcon sx={{ fontSize: '0.85rem', color: 'text.disabled' }} />
                )}
                <span>{facilityLabel(item.fulfillingPharmacy)}</span>
                <Typography variant='caption' color='text.disabled' sx={{ ml: 'auto', pl: 2 }}>
                  {item.isLockForCount
                    ? 'Locked for count'
                    : `${result?.totalWipQty ?? 0} reserved`}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography component='span' sx={FIELD_LABEL_SX}>
          What to return
        </Typography>
        {renderLocationScope()}

        {/* Directly under the picker it reflects — a live number belongs next to the control
            that changes it. */}
        <Box
          sx={{
            ...PANEL_SX,
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography variant='body2' color='text.secondary'>
            {scopedLotCount === 0
              ? 'Nothing selected'
              : `Recalling ${scopedLotCount} ${scopedLotCount === 1 ? 'lot' : 'lots'}`}
          </Typography>
          <Typography variant='h6' sx={{ fontWeight: 700 }}>
            {`${scopedWip} units`}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography component='span' sx={FIELD_LABEL_SX}>
          Reason *
        </Typography>
        <TextField
          select
          size='small'
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          sx={{ width: 380 }}
          inputProps={{ 'aria-label': 'Reason' }}
        >
          {RECALL_REASONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      </Box>

      <Box sx={{ mb: 2 }}>
        <Typography component='span' sx={FIELD_LABEL_SX}>
          Note (optional)
        </Typography>
        <TextField
          size='small'
          fullWidth
          value={note}
          onChange={(event) => setNote(event.target.value)}
          inputProps={{ 'aria-label': 'Note' }}
        />
      </Box>

      {/* Gates the action rather than merely warning — this is the irreversible step. */}
      <FormControlLabel
        sx={ACK_SX}
        control={
          <Checkbox
            size='small'
            checked={acknowledged}
            onChange={(event) => setAcknowledged(event.target.checked)}
            sx={{
              py: 0.25,
              color: colors.inventoryLockOrange,
              '&.Mui-checked': { color: colors.inventoryLockOrange },
            }}
            inputProps={{ 'aria-label': 'Acknowledge unfulfillment' }}
          />
        }
        label={
          <Box sx={{ py: 0.25 }}>
            <Typography variant='body2' sx={{ fontWeight: 700 }}>
              I understand this will unfulfill the affected orders.
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{ mt: 0.25 }}>
              The prescription line items holding this stock will return to the fill queue and their
              orders will go back to <strong>OPEN</strong>. This cannot be undone from this screen.
            </Typography>
          </Box>
        }
      />
    </Box>
  );

  return (
    <Dialog
      open={open}
      // A stray backdrop click must not discard the scope, or dismiss a run already in flight.
      onClose={(event, closeReason) => {
        if (closeReason === 'backdropClick') return;
        handleClose();
      }}
      disableEscapeKeyDown
      PaperProps={{ sx: { width: 720, maxWidth: 720 } }}
    >
      <RecallDialogHeader
        title={outcome ? 'Stock Returned' : 'Return Allocated Stock to Inventory'}
        item={first.item}
        locationCount={summaries.length}
        closeDisabled={submitting}
        onClose={handleClose}
      />

      <Divider />

      {outcome ? <RecallResultPanel outcome={outcome} /> : renderForm()}

      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: outcome ? 'flex-end' : 'space-between',
        }}
      >
        {outcome ? (
          <Button variant='contained' onClick={handleClose}>
            Close
          </Button>
        ) : (
          <>
            <RecallHowItWorks />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button onClick={handleClose} color='inherit' disabled={submitting}>
                Cancel
              </Button>
              <Button
                variant='contained'
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                endIcon={submitting ? <CircularProgress size={14} color='inherit' /> : null}
              >
                {submitting ? 'Returning…' : 'Return stock'}
              </Button>
            </Box>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

RecallAllocationsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  // One entry per pharmacy section the drawer already loaded — no request before confirm.
  summaries: PropTypes.arrayOf(
    PropTypes.shape({
      item: PropTypes.shape({
        inventoryItemId: PropTypes.string,
        ndc: PropTypes.string,
        drugName: PropTypes.string,
        strength: PropTypes.string,
        manufacturer: PropTypes.string,
        isLockForCount: PropTypes.bool,
        fulfillingPharmacy: PropTypes.shape({
          fulfillingPharmacyId: PropTypes.string,
          name: PropTypes.string,
          state: PropTypes.string,
        }),
      }),
      result: PropTypes.shape({
        totalWipQty: PropTypes.number,
        recallInProgress: PropTypes.bool,
        lots: PropTypes.arrayOf(PropTypes.shape({})),
      }),
    })
  ),
  onClose: PropTypes.func.isRequired,
  onRecallSuccess: PropTypes.func,
};
RecallAllocationsDialog.defaultProps = { summaries: [], onRecallSuccess: () => {} };

export default RecallAllocationsDialog;
